/**
 * AccountPage - User account settings page.
 *
 * Contains profile editing (name) and password change sections,
 * each in their own Card for visual separation.
 */

import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';
import { ApiError } from '@/lib/api';
import { useAuth, useUpdateProfile, useChangePassword } from '@/features/auth';
import { AccountSettingsForm } from '@/features/auth/components/organisms/AccountSettingsForm';
import { ChangePasswordForm } from '@/features/auth/components/organisms/ChangePasswordForm';

export function AccountPage() {
  const { user } = useAuth();
  const profileMutation = useUpdateProfile();
  const passwordMutation = useChangePassword();

  if (!user) return null;

  const handleProfileSubmit = async (data: { name?: string | null }) => {
    await profileMutation.mutateAsync(data);
  };

  const handlePasswordSubmit = async (currentPassword: string, newPassword: string) => {
    await passwordMutation.mutateAsync({ currentPassword, newPassword });
  };

  const profileError =
    profileMutation.error instanceof ApiError
      ? profileMutation.error.message
      : profileMutation.error
        ? String(profileMutation.error)
        : null;
  const passwordError =
    passwordMutation.error instanceof ApiError
      ? passwordMutation.error.message
      : passwordMutation.error
        ? String(passwordMutation.error)
        : null;

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <h1 className="text-2xl font-bold text-text-primary">Account settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <AccountSettingsForm
            user={user}
            onSubmit={handleProfileSubmit}
            isLoading={profileMutation.isPending}
            isSuccess={profileMutation.isSuccess}
            error={profileError}
            onDismissError={profileMutation.reset}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
        </CardHeader>
        <CardContent>
          <ChangePasswordForm
            onSubmit={handlePasswordSubmit}
            isLoading={passwordMutation.isPending}
            isSuccess={passwordMutation.isSuccess}
            error={passwordError}
            onDismissError={passwordMutation.reset}
            onCancel={passwordMutation.isSuccess ? passwordMutation.reset : undefined}
          />
        </CardContent>
      </Card>
    </div>
  );
}
